import { Box, Button, IconButton, Typography, useTheme } from '@mui/material';
import {
  createElement,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import FormFields from '../form/FormFields';
import findDifferences from './findDifferencesInDataCollection';
import { useTranslation } from 'react-i18next';
import ExoAlert from './ExoAlert';
import Loader from '../special/Loader';
import ExoDraftManager from './ExoDraftManager';
import DeleteIcon from '@mui/icons-material/Delete';
import { tokens } from '../../global/theme/tokens';

const ExoForm = forwardRef(
  (
    {
      fields,
      onSubmit = () => {},
      onDelete,
      onCancel,
      onChange = () => {},
      debug = false,
      startDataCollection,
      header = '',
      validationSchema,
      submitText,
      noControls = false,
      sx = {},
      className,
      dialog,
      noDifferences,
      alert = {},
      useStartDataOnReset,
      isLoading,
      noEnter,
      draftType,
      outerClassName,
      isNotValid,
      param,
      titleFunction,
      size,
      noAutoReset,
      externalErrors,
    },
    ref
  ) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
    const { t } = useTranslation();
    const [dataCollection, setDataCollection] = useState({});
    const [alertOpen, setAlertOpen] = useState(false);

    const updateDataCollection = (insertData) => {
      setDataCollection((prev) => ({
        ...prev,
        ...insertData,
      }));
    };

    useEffect(() => {
      if (!startDataCollection) return;
      setDataCollection((prev) => ({
        ...prev,
        ...startDataCollection,
      }));
    }, [startDataCollection]);

    function handleSubmit() {
      setAlertOpen(false);
      if (noDifferences) {
        onSubmit(dataCollection);
      } else {
        onSubmit(findDifferences(startDataCollection, dataCollection));
      }
    }

    function requestSubmit() {
      if (alert.message) {
        setAlertOpen(true);
      } else {
        handleSubmit();
      }
    }

    function handleReset() {
      setDataCollection(useStartDataOnReset ? startDataCollection : {});
    }

    // validation --------------------------
    const [valid, setValid] = useState(false);

    const validateDataCollection = async () => {
      if (!validationSchema) {
        setValid(true);
        onChange(dataCollection, true);
        return;
      }
      try {
        await validationSchema.validate(dataCollection);
        if (isNotValid) throw new Error('Validation failed: isNotValid');
        // validation passed for the specific field
        setValid(true);
        onChange(dataCollection, true);
      } catch (err) {
        // validation failed for the specific field
        if (debug) console.log('NOT VALID', err);
        onChange(dataCollection, false);
        setValid(false);
      }
    };

    // enter actions
    const [preventEnter, setPreventEnter] = useState(noEnter);

    useEffect(() => {
      setPreventEnter(noEnter);
    }, [noEnter]);

    useEffect(() => {
      validateDataCollection();

      const handleKeyDown = (event) => {
        if (event.key === 'Enter' && valid && !preventEnter) {
          event.preventDefault(); // Prevent default form submission behavior
          //requestSubmit();
        }
      };

      window.addEventListener('keydown', handleKeyDown);

      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }, [dataCollection, valid, preventEnter, isNotValid]);

    // exposed functions
    useImperativeHandle(ref, () => ({
      reset: handleReset,
      isValid: () => valid,
      updateDataCollection: updateDataCollection,
      dataCollection,
      setPreventEnter,
    }));

    const classes = dialog
      ? ' flex flex-col gap-4 p-2 relative'
      : 'relative flex flex-col gap-4 pt-2';

    const [requestDelete, setRequestDelete] = useState(null);
    function handleRequestDelete() {
      setRequestDelete({
        message: t('Are you sure you want to delete this entry?'),
        color: 'error',
      });
    }

    return (
      <Box
        className={
          outerClassName ||
          'flex flex-col gap-4 relative h-full overflow-y-auto'
        }
      >
        <Box>
          {onDelete && startDataCollection?.id && (
            <IconButton
              onClick={handleRequestDelete}
              color="error"
              className="flex"
              sx={{
                border: `1px solid ${colors.error}80`,
                borderRadius: '9999px',

                '.delete-text': {
                  maxWidth: '0px',
                  overflow: 'hidden',
                  transition: '0.3s ease',
                  opacity: '0',
                },
                ':hover .delete-text': {
                  maxWidth: '150px',
                  transition: '0.3s ease',
                  paddingLeft: '10px',
                  paddingRight: '5px',
                  opacity: '1',
                },
              }}
            >
              <DeleteIcon />
              <Typography className="delete-text">{t('Delete')}</Typography>
            </IconButton>
          )}
          {draftType && (
            <ExoDraftManager
              draftType={draftType}
              dataCollection={dataCollection}
              onChange={updateDataCollection}
              startDataCollection={startDataCollection}
              setDataCollection={setDataCollection}
              titleFunction={titleFunction}
            />
          )}
        </Box>
        {isLoading && <Loader active={isLoading} noBlur />}
        {header && (
          <Typography variant="h3" textAlign="center" className="  py-2">
            {header}
          </Typography>
        )}
        <Box className={className ? className : classes} sx={sx}>
          {debug && (
            <Button
              color="warning"
              variant="contained"
              className=" z-40"
              sx={{
                position: 'absolute',
              }}
              onClick={() =>
                console.log(
                  findDifferences(startDataCollection, dataCollection),
                  dataCollection
                )
              }
            >
              LOG
            </Button>
          )}

          {createElement(FormFields, {
            dataCollection,
            startDataCollection: startDataCollection || {},
            updateDataCollection,
            fields,
            validationSchema,
            onPreventEnter: setPreventEnter,
            param,
            size,
            externalErrors,
          })}
        </Box>
        {!noControls && (
          <FormControls
            handleReset={noAutoReset ? () => {} : handleReset}
            onSubmit={requestSubmit}
            isValid={valid}
            onCancel={onCancel}
            submitText={submitText}
          />
        )}
        <ExoAlert
          open={alertOpen}
          onSubmit={handleSubmit}
          onClose={() => setAlertOpen(false)}
          alert={alert}
        />
        <ExoAlert
          open={Boolean(requestDelete)}
          onSubmit={() => {
            setRequestDelete(null);
            onDelete(startDataCollection);
          }}
          onClose={() => setRequestDelete(null)}
          alert={requestDelete || {}}
        />
      </Box>
    );
  }
);

export const FormControls = ({
  onSubmit,
  onCancel,
  submitText,
  isValid,
  handleReset,
}) => {
  const { t } = useTranslation();
  function handleCancel() {
    handleReset();
    if (onCancel) onCancel();
  }
  return (
    <Box
      className={
        'flex flex-row w-full items-center ' +
        (onCancel ? 'justify-between' : 'justify-end')
      }
    >
      {onCancel && (
        <Button onClick={handleCancel} variant="outlined" color="error">
          {t('Cancel')}
        </Button>
      )}
      <Button
        onClick={onSubmit}
        variant="contained"
        color="success"
        disabled={!isValid}
        sx={{ marginBottom: 1 }}
      >
        {submitText ? submitText : t('Save')}
      </Button>
    </Box>
  );
};

export default ExoForm;
